/*
 * <advertorial-countdown>
 * ------------------------------------------------------------------
 * Ticking remainder for the advertorial announcement bar.
 *
 * Not `countdown-timer` from assets/countdown.js, which this deliberately does
 * not extend or replace: that element is hard-wired to a four-cell days /
 * hours / minutes / seconds block, its stylesheet is scoped to `#Message-bar-*`
 * selectors, and the butter bar depends on both. Re-shaping it to also produce
 * an inline `mm:ss` string would put the site-wide announcement bar at risk for
 * a landing-page detail. This is ~70 lines and owns nothing else.
 *
 * Two modes:
 *   deadline — counts to a real date. The date is the truth; every visitor sees
 *              the same remainder, and it reaches zero for good.
 *   session  — counts down a fixed duration, persisted in sessionStorage so it
 *              survives navigation within the visit instead of restarting on
 *              every page view. See the note on the section's `timer_mode`
 *              setting about what the FTC expects of a timer like this.
 *
 * Accessibility
 *   The ticking string is `aria-hidden`. A per-second live region would talk
 *   over everything else on the page, and a screen reader user gains nothing
 *   from hearing "fourteen fifty-eight" once a second. The equivalent
 *   information — the actual deadline, or the fact that the offer is
 *   time-limited — is rendered once as visually hidden text by the section.
 *   Under WCAG 2.2.2 the tick is exempt as essential: it *is* the information.
 *
 * Layout
 *   Digits are tabular and the value box has a reserved min-width in CSS, so
 *   the bar never reflows as the number of glyphs changes.
 */
class AdvertorialCountdown extends HTMLElement {
  connectedCallback() {
    this.valueEl = this.querySelector('[data-countdown-value]');
    if (!this.valueEl) return;

    this.format = this.dataset.format || 'auto';
    this.onExpire = this.dataset.onExpire || 'hide-timer';
    this.expiredText = this.dataset.expiredText || '';
    this.endsAt = this.resolveEnd();

    if (this.endsAt === null) {
      this.expire();
      return;
    }

    this.tick();
    // A plain interval rather than rAF: this updates once a second, and rAF
    // would wake the compositor 60 times for every one of those.
    this.timer = setInterval(() => this.tick(), 1000);
    // Catch up immediately when the tab comes back, since intervals are
    // throttled in the background and the reading would otherwise be stale.
    this.onVisibility = () => {
      if (!document.hidden) this.tick();
    };
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /* Epoch milliseconds the countdown runs to, or null if it is already over. */
  resolveEnd() {
    if (this.dataset.mode === 'session') {
      const ms = (parseInt(this.dataset.duration, 10) || 0) * 1000;
      if (ms <= 0) return null;
      const key = this.dataset.storageKey;
      let stored = null;
      // Private-mode Safari throws on sessionStorage access rather than
      // returning null, so every touch of it is guarded.
      try {
        stored = parseInt(sessionStorage.getItem(key), 10);
      } catch (e) {
        /* storage unavailable — fall through to a fresh window */
      }
      if (stored && stored > Date.now()) return stored;
      const end = Date.now() + ms;
      try {
        sessionStorage.setItem(key, String(end));
      } catch (e) {
        /* not persisting is fine; the timer still runs for this page */
      }
      return end;
    }

    const parsed = Date.parse(this.dataset.deadline || '');
    if (Number.isNaN(parsed) || parsed <= Date.now()) return null;
    return parsed;
  }

  tick() {
    if (document.hidden) return;

    const remaining = this.endsAt - Date.now();
    if (remaining <= 0) {
      this.expire();
      return;
    }

    const total = Math.floor(remaining / 1000);
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const pad = (n) => String(n).padStart(2, '0');

    let out;
    if (this.format === 'dhms' || (this.format === 'auto' && days > 0)) {
      out = `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    } else if (this.format === 'hhmmss' || (this.format === 'auto' && hours > 0)) {
      out = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    } else {
      // `mmss` clamps rather than wrapping: a two-hour window shows 120:00, not
      // 00:00, so the string can never understate the time left.
      out = `${pad(minutes + hours * 60 + days * 1440)}:${pad(seconds)}`;
    }

    this.valueEl.textContent = out;
  }

  expire() {
    clearInterval(this.timer);
    if (this.onExpire === 'hide-bar') {
      const bar = this.closest('[data-countdown-host]');
      if (bar) bar.hidden = true;
      return;
    }
    if (this.onExpire === 'message' && this.expiredText) {
      // Replaces the whole timer, label included — "Ends in: Offer ended" is
      // not a sentence anyone wants to read.
      this.textContent = this.expiredText;
      return;
    }
    this.hidden = true;
  }
}

if (!customElements.get('advertorial-countdown')) {
  customElements.define('advertorial-countdown', AdvertorialCountdown);
}
